import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Upload, Trash2, Image as ImageIcon, Loader2, Check } from "lucide-react";

/**
 * PhotoManager — handles logo / facade / gallery uploads for cafe owner.
 * Files render via authenticated blob URLs (Bearer header).
 */
function AuthImage({ fileId, alt, className = "" }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const blob = await res.blob();
        const u = URL.createObjectURL(blob);
        revoke = u;
        setBlobUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);

  if (!blobUrl) return <div className={`bg-[#141210] animate-pulse ${className}`} />;
  return <img src={blobUrl} alt={alt} className={className} />;
}

export default function PhotoManager({ cafe, onChange }) {
  const [files, setFiles] = useState([]);
  const [limit, setLimit] = useState({ used: 0, limit: 3, plan_id: null });
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  const logoInput = useRef(null);
  const facadeInput = useRef(null);
  const galleryInput = useRef(null);

  const load = async () => {
    const [f, l] = await Promise.all([api.get("/me/files"), api.get("/me/gallery-limit").catch(() => ({ data: null }))]);
    setFiles(f.data);
    if (l.data) setLimit(l.data);
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file, purpose) => {
    setErr(""); setBusy((b) => ({ ...b, [purpose]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("purpose", purpose);
      const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      // If logo or facade, bind to cafe immediately
      if (purpose === "logo") {
        await api.put("/me/cafe", { ...cafe, logo_url: data.url });
        onChange?.({ logo_url: data.url });
      } else if (purpose === "facade") {
        await api.put("/me/cafe", { ...cafe, banner_url: data.url });
        onChange?.({ banner_url: data.url });
      }
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Upload échoué");
    } finally {
      setBusy((b) => ({ ...b, [purpose]: false }));
    }
  };

  const handleFile = (e, purpose) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file, purpose);
    e.target.value = "";
  };

  const handleGallery = (e) => {
    const arr = Array.from(e.target.files || []);
    arr.forEach((f) => uploadFile(f, "gallery"));
    e.target.value = "";
  };

  const remove = async (fileId, purpose) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    await api.delete(`/me/files/${fileId}`);
    if (purpose === "logo") {
      await api.put("/me/cafe", { ...cafe, logo_url: "" });
      onChange?.({ logo_url: "" });
    } else if (purpose === "facade") {
      await api.put("/me/cafe", { ...cafe, banner_url: "" });
      onChange?.({ banner_url: "" });
    }
    await load();
  };

  const logoFile = files.find((f) => f.purpose === "logo");
  const facadeFile = files.find((f) => f.purpose === "facade");
  const gallery = files.filter((f) => f.purpose === "gallery");

  return (
    <div data-testid="photo-manager" className="stat-card">
      <div className="overline mb-4">Photos · JPG / PNG / WEBP · max 5 Mo · Nettes, lisibles, professionnelles</div>
      {err && <div className="text-red-400 text-sm mb-4" data-testid="photo-error">{err}</div>}

      <div className="grid sm:grid-cols-2 gap-6">
        {/* Logo */}
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#A8A39D] mb-3">Logo</div>
          <div className="aspect-square bg-[#141210] border border-white/5 flex items-center justify-center relative group">
            {logoFile ? <AuthImage fileId={logoFile.id} alt="Logo" className="w-full h-full object-cover" /> : <ImageIcon className="text-[#5D4037]" size={32} />}
            {logoFile && (
              <button data-testid="del-logo" onClick={() => remove(logoFile.id, "logo")} className="absolute top-2 right-2 p-1.5 bg-black/60 text-red-300 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <input ref={logoInput} data-testid="logo-input" type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => handleFile(e, "logo")} />
          <button data-testid="upload-logo" onClick={() => logoInput.current?.click()} disabled={busy.logo} className="btn-ghost mt-3 w-full">
            {busy.logo ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {logoFile ? "Remplacer" : "Téléverser"}
          </button>
        </div>

        {/* Façade */}
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#A8A39D] mb-3">Photo de façade (bannière)</div>
          <div className="aspect-square bg-[#141210] border border-white/5 flex items-center justify-center relative group">
            {facadeFile ? <AuthImage fileId={facadeFile.id} alt="Façade" className="w-full h-full object-cover" /> : <ImageIcon className="text-[#5D4037]" size={32} />}
            {facadeFile && (
              <button data-testid="del-facade" onClick={() => remove(facadeFile.id, "facade")} className="absolute top-2 right-2 p-1.5 bg-black/60 text-red-300 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <input ref={facadeInput} data-testid="facade-input" type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => handleFile(e, "facade")} />
          <button data-testid="upload-facade" onClick={() => facadeInput.current?.click()} disabled={busy.facade} className="btn-ghost mt-3 w-full">
            {busy.facade ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {facadeFile ? "Remplacer" : "Téléverser"}
          </button>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-[11px] uppercase tracking-widest text-[#A8A39D]">
            Galerie ({gallery.length}<span className="text-[#C5A059]">/{limit.limit}</span>)
            {limit.plan_id && <span className="ml-3 text-[10px] text-[#C5A059] uppercase">Plan {limit.plan_id}</span>}
            {!limit.plan_id && <span className="ml-3 text-[10px] text-orange-400 uppercase">Sans abonnement — quota réduit</span>}
          </div>
          <input ref={galleryInput} data-testid="gallery-input" type="file" multiple accept="image/jpeg,image/png,image/webp" hidden onChange={handleGallery} />
          <button data-testid="upload-gallery" onClick={() => galleryInput.current?.click()} disabled={busy.gallery || gallery.length >= limit.limit} className="btn-ghost text-xs">
            {busy.gallery ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {gallery.length >= limit.limit ? "Limite atteinte" : "Ajouter"}
          </button>
        </div>
        {gallery.length >= limit.limit && limit.plan_id === "standard" && (
          <div className="mb-3 text-xs text-orange-400 border border-orange-400/30 bg-orange-400/5 p-3">
            Vous avez atteint la limite Standard (15 photos). <a href="/dashboard/billing" className="underline text-[#C5A059]">Passez au plan Premium</a> pour 30 photos.
          </div>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {gallery.map((f) => (
            <div key={f.id} className="aspect-square bg-[#141210] relative group">
              <AuthImage fileId={f.id} alt="" className="w-full h-full object-cover" />
              <button data-testid={`del-${f.id}`} onClick={() => remove(f.id, "gallery")} className="absolute top-1 right-1 p-1 bg-black/60 text-red-300 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {!gallery.length && <div className="col-span-full text-[#A8A39D] text-sm py-6 text-center border border-white/5">Aucune photo de galerie</div>}
        </div>
      </div>
    </div>
  );
}
